import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useUnit } from "effector-react";
import { ROUTES } from "@/core/constants/app";
import "./Sidebar.scss";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { imageList } from "@/core/utils/imageList";
import { logoutRequested } from "@/features/auth/store/auth";
import {
  $unreadNotificationsCount,
  fetchNotificationsRequested,
} from "@/features/notifications/store";
import { $unreadConversationsCount } from "@/features/chat/store/chatStore";
import NotificationsModal from "./NotificationsModal";
import {
  $userBasicInfo,
  $userProfile,
  fetchUserProfileRequested,
} from "@/features/user/store";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { setIsMobileNavOpen } from "@/core/store/app";

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadNotificationsCount] = useUnit([$unreadNotificationsCount]);
  const [unreadChatsCount] = useUnit([$unreadConversationsCount]);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] =
    useState(false);
  const userBasicInfo = useUnit($userBasicInfo);
  const userProfile = useUnit($userProfile);
  const { pro_user } = userBasicInfo || {};

  // Fetch user profile and notifications on component mount
  useEffect(() => {
    fetchUserProfileRequested();
    fetchNotificationsRequested();
  }, []);

  // Note: Notification polling removed - now using real-time Mercure updates
  // New notifications are automatically fetched when Mercure receives "new_notification" events

  // Get user avatar from profile

  const mainNavItems = [
    {
      path: ROUTES.HOME,
      label: "Home",
      icon: "icon-home",
      active: location.pathname === ROUTES.HOME,
      isPro: false,
    },
    {
      path: ROUTES.CALLS,
      label: "Calls",
      icon: "icon-call-page",
      badge: "23",
      badgeType: "primary",
      active: location.pathname === ROUTES.CALLS,
      isPro: false,
    },
    {
      path: ROUTES.PARTNERS_ORGANIZATIONS,
      label: "Partners",
      icon: "icon-partners",
      active: location.pathname === ROUTES.PARTNERS_ORGANIZATIONS,
      isPro: false,
    },
    {
      path: ROUTES.IDEAS,
      label: "Ideas",
      icon: "icon-ideas",
      badge: "5",
      active: location.pathname === ROUTES.IDEAS,
      isPro: false,
    },
    {
      path: ROUTES.GOALS,
      label: "Goals",
      icon: "icon-goals",
      active: location.pathname === ROUTES.GOALS,
      isPro: true,
    },
  ];

  const handleLogout = () => {
    logoutRequested();
  };

  const handleNotificationsClick = useCallback(() => {
    setIsNotificationsModalOpen((prev) => !prev);
  }, []);

  const handleNotificationsClose = useCallback(() => {
    setIsNotificationsModalOpen(false);
  }, []);

  const handleChatsClick = useCallback(() => {
    navigate(ROUTES.CHAT_INBOX);
    setIsMobileNavOpen(false);
  }, [navigate]);

  return (
    <div className="app-sidebar">
      <div className="sidebar-nav">
        <div className="nav-section">
          <div className="nav-items">
            {mainNavItems.map((item) => {
              // if (item.isPro && !pro_user) return <></>;
              return (
                <Link
                  to={item.path}
                  key={item.path}
                  className={`nav-item ${item.active ? "active" : ""}`}
                >
                  <div className="nav-icon">
                    <SvgIcon icon={item.icon} />
                  </div>
                  <div className="">{item.label}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      <div className="sidebar-footer">
        <div className="footer-item" onClick={handleChatsClick}>
          <div className="footer-icon-wrapper">
            <SvgIcon icon="icon-message" />
            {unreadChatsCount > 0 && (
              <div className="notification-badge">{unreadChatsCount}</div>
            )}
          </div>
        </div>
        <div className="footer-item" onClick={handleNotificationsClick}>
          <div className="footer-icon-wrapper">
            <SvgIcon icon="icon-bell" />
            {unreadNotificationsCount > 0 && (
              <div className="notification-badge">
                {unreadNotificationsCount}
              </div>
            )}
          </div>
        </div>
        <div className="footer-item">
          <Link to={"/settings"}>
            <div className="user-avatar">
              <BgImage
                img={
                  userProfile &&
                  typeof userProfile.user_data.user_picture === "object" &&
                  userProfile?.user_data?.user_picture?.[0]?.url
                    ? userProfile.user_data.user_picture?.[0]?.url
                    : imageList.noimage
                }
              />

              <div className="status-indicator"></div>
            </div>
          </Link>
        </div>

        <div className="footer-item logout-item" onClick={handleLogout}>
          <div className="footer-icon-wrapper">
            <SvgIcon icon="icon-logout" />
          </div>
        </div>
      </div>

      <NotificationsModal
        isOpen={isNotificationsModalOpen}
        onClose={handleNotificationsClose}
      />
    </div>
  );
};

export default Sidebar;
